import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Avatar
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import api from '../../utils/api';
import { getAvatarSrc } from '../../utils/avatarConstants';
import { 
  User, 
  Bell, 
  Lock, 
  Eye, 
  EyeSlash, 
  SignOut, 
  Trash,
  FileText,
  Camera
} from '@phosphor-icons/react';
import LegalDocumentModal from '../Legal/LegalDocumentModal';
import useThemeStyles from '../../theme/useThemeStyles';

function SupplierSettings() {
  const styles = useThemeStyles();
  const { user, logout, setUser } = useAuth();
  const { pushSubscribeError, clearPushSubscribeError } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [notificationPreferences, setNotificationPreferences] = useState({
    newMessages: { email: true, push: true },
    bookingRequests: { email: true, push: true },
    formSubmissions: { email: true, push: true },
    reminders: { email: true, push: true },
    platformUpdates: { email: true, push: true }
  });
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profilePicture, setProfilePicture] = useState('');
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);

  useEffect(() => {
    fetchUserData();
    loadLegalDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [user]); // Re-fetch when user changes

  const loadLegalDocuments = async () => {
    try {
      const response = await api.get('/api/legal', { params: { userType: 'supplier' } });
      setLegalDocuments(response.data.documents || []);
    } catch (err) {}
  };

  const handleLegalDocClick = (doc) => {
    setSelectedDocument(doc);
    setLegalModalOpen(true);
  };

  const fetchUserData = async () => {
    // Use user data from AuthContext directly since it's already loaded
    if (user) {
      setUserData({
        id: user._id || user.id,
        email: user.email,
        role: user.role,
        isEmailVerified: user.isEmailVerified,
        createdAt: user.createdAt,
        firstName: user.firstName,
        lastName: user.lastName,
        settings: user.settings || {}
      });
      setProfilePicture(user.profilePicture || '');
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || ''
      });
      if (user.settings?.notificationTypes) {
        setNotificationPreferences((prev) => ({
          ...prev,
          ...user.settings.notificationTypes
        }));
      }

      try {
        const res = await api.get('/api/users/profile');
        const p = res.data;
        setUserData({
          id: p._id || user._id || user.id,
          email: p.email,
          role: p.role,
          isEmailVerified: p.isEmailVerified,
          createdAt: p.createdAt,
          firstName: p.firstName,
          lastName: p.lastName,
          settings: p.settings || user.settings || {}
        });
        setProfilePicture(p.profilePicture || '');
        setProfileForm({
          firstName: p.firstName || '',
          lastName: p.lastName || ''
        });
        if (p.settings?.notificationTypes) {
          setNotificationPreferences((prev) => ({
            ...prev,
            ...p.settings.notificationTypes
          }));
        }
      } catch {
        // Keep context-based values
      }
    } else {
      // Fallback: try to fetch from API if user context is not available
      try {
        const response = await api.get('/api/supplier/account');
        setUserData(response.data);
        if (response.data?.notificationPreferences) {
          setNotificationPreferences((prev) => ({
            ...prev,
            ...response.data.notificationPreferences
          }));
        }
        
        if (response.data) {
          setProfileForm({
            firstName: response.data.firstName || '',
            lastName: response.data.lastName || ''
          });
        }
        try {
          const pr = await api.get('/api/users/profile');
          setProfilePicture(pr.data.profilePicture || '');
        } catch {
          setProfilePicture('');
        }
      } catch (err) {
        setError('Failed to load user data');
      }
    }
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Only PNG and JPEG image files are allowed.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB.');
      event.target.value = '';
      return;
    }

    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      setUploadingProfileImage(true);
      setError('');
      setSuccess('');

      const response = await api.post('/api/supplier/account-profile-image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const url = response.data.profilePicture || response.data.avatarUrl;
      setProfilePicture(url || '');
      if (setUser) {
        setUser((prev) => (prev ? { ...prev, profilePicture: url } : prev));
      }
      setSuccess('Profile image updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || '';
      setError(msg || 'Failed to upload profile image.');
      event.target.value = '';
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      await api.put('/api/supplier/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      setSuccess('Password updated successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = (typeKey, channel) => (event) => {
    const checked = event.target.checked;
    setNotificationPreferences((prev) => ({
      ...prev,
      [typeKey]: {
        ...(prev[typeKey] || {}),
        [channel]: checked
      }
    }));
  };

  const saveNotificationPreferences = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/api/supplier/email-preferences', {
        notificationPreferences
      });

      const hasEmailEnabled = Object.values(notificationPreferences).some((type) => type?.email);
      const hasPushEnabled = Object.values(notificationPreferences).some((type) => type?.push);
      if (setUser) {
        setUser((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            settings: {
              ...(prev.settings || {}),
              notifications: {
                ...(prev.settings?.notifications || {}),
                email: hasEmailEnabled,
                push: hasPushEnabled
              },
              notificationTypes: notificationPreferences
            }
          };
        });
      }

      setSuccess('Notification preferences updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleProfileChange = (field) => (e) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleProfileSubmit = async () => {
    setSavingProfile(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim()
      };

      // Use supplier-specific endpoint for updating user profile
      const response = await api.put('/api/supplier/user-profile', updateData);
      
      // Update user data
      if (response.data) {
        setUserData(prev => ({
          ...prev,
          firstName: response.data.firstName,
          lastName: response.data.lastName
        }));
      }
      
      // Update profile form with latest data
      setProfileForm({
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || ''
      });
      
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading && !userData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading account information...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 4 }}>
        Account Settings
      </Typography>
        
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
        
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
        
      <Grid container spacing={4}>
        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <User size={24} weight="regular" style={{ marginRight: 12 }} />
              <Typography variant="h6">
                Account Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Profile image
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Avatar
                  src={getAvatarSrc(profilePicture)}
                  alt=""
                  sx={{
                    width: 88,
                    height: 88,
                    fontSize: '1.75rem',
                    bgcolor: 'primary.main'
                  }}
                >
                  {(profileForm.firstName?.charAt(0) || profileForm.lastName?.charAt(0) || user?.email?.charAt(0) || '?').toUpperCase()}
                </Avatar>
                <Box>
                  <input
                    accept="image/png,image/jpeg,image/jpg"
                    style={{ display: 'none' }}
                    id="supplier-profile-image-upload"
                    type="file"
                    onChange={handleProfileImageUpload}
                    disabled={uploadingProfileImage}
                  />
                  <label htmlFor="supplier-profile-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploadingProfileImage ? <CircularProgress size={18} /> : <Camera size={20} weight="regular" />}
                      disabled={uploadingProfileImage}
                    >
                      {uploadingProfileImage ? 'Uploading…' : 'Upload image'}
                    </Button>
                  </label>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: 280 }}>
                    PNG or JPEG, up to 5MB. Stored securely on our servers.
                  </Typography>
                </Box>
              </Box>
            </Box>
              
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileForm.firstName}
                  onChange={handleProfileChange('firstName')}
                  disabled={savingProfile}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileForm.lastName}
                  onChange={handleProfileChange('lastName')}
                  disabled={savingProfile}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={userData?.email || user?.email || ''}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  To change your email address, please contact support
                </Typography>
              </Grid>
                
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Type"
                  value="Supplier"
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
                
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Member Since"
                  value={userData?.createdAt || user?.createdAt ? new Date(userData?.createdAt || user?.createdAt).toLocaleDateString() : 'Not available'}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleProfileSubmit}
                  disabled={savingProfile}
                  sx={{ mt: 1 }}
                >
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
            
          {/* Notifications */}
          <Paper elevation={0} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Bell size={24} weight="regular" style={{ marginRight: 12 }} />
              <Typography variant="h6">
                Notifications
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {pushSubscribeError && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={clearPushSubscribeError}>
                Push notifications: {pushSubscribeError}
              </Alert>
            )}

            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Push notifications</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { key: 'newMessages', label: 'New messages from couples' },
                  { key: 'bookingRequests', label: 'New booking requests' },
                  { key: 'formSubmissions', label: 'New form submissions' },
                  { key: 'reminders', label: 'Reminder notifications' },
                  { key: 'platformUpdates', label: 'Platform updates' }
                ].map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={Boolean(notificationPreferences[row.key]?.email)}
                        onChange={handleNotificationToggle(row.key, 'email')}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={Boolean(notificationPreferences[row.key]?.push)}
                        onChange={handleNotificationToggle(row.key, 'push')}
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button
              variant="contained"
              color="primary"
              onClick={saveNotificationPreferences}
              disabled={loading}
            >
              Save notification preferences
            </Button>
          </Paper>
        </Grid>
          
        {/* Password Change */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Lock size={24} weight="regular" style={{ marginRight: 12 }} />
              <Typography variant="h6">
                Change Password
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
              
            <form onSubmit={handlePasswordSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={showPassword ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                  
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    required
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                  
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                  
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
            
          {/* Legal Documents */}
          <Paper elevation={0} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FileText size={24} weight="regular" style={{ marginRight: 12 }} />
              <Typography variant="h6">
                Legal Documents
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
              
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              View all terms, policies, and legal documents
            </Typography>
              
            <Grid container spacing={2}>
              {legalDocuments.map((doc) => (
                <Grid item xs={12} sm={6} key={doc.fileName}>
                  <Button
                    onClick={() => handleLegalDocClick(doc)}
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {doc.buttonText}
                  </Button>
                </Grid>
              ))}
              {legalDocuments.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    No legal documents available. Documents will appear here once configured in admin.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
            
          {/* Account Actions */}
          <Paper elevation={0} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <User size={24} weight="regular" style={{ marginRight: 12 }} />
              <Typography variant="h6">
                Account Actions
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
              
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<SignOut />}
                  onClick={handleLogout}
                  sx={{ mb: 2 }}
                >
                  Log Out
                </Button>
                  
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<Trash />}
                  onClick={() => {
                    // Show confirmation dialog before deleting account
                    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                      // Handle account deletion
                      alert('Please contact support to delete your account');
                    }
                  }}
                >
                  Delete Account
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Legal Document Modal */}
      {selectedDocument && (
        <LegalDocumentModal
          open={legalModalOpen}
          onClose={() => {
            setLegalModalOpen(false);
            setSelectedDocument(null);
          }}
          fileName={selectedDocument.fileName}
          title={selectedDocument.title}
        />
      )}
    </Box>
  );
}

export default SupplierSettings; 